import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Checkbox, FormControlLabel, Paper, Tab, Tabs, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import OpeningImportPanel from './openingBalances/OpeningImportPanel';
import { OpeningBalanceService } from '../../services/openingBalance.service';
import {
    OpeningBalanceStatus, OpeningDocumentsResult, OpeningTrialBalanceResult,
} from '../../interfaces/openingBalance.interface';
import { parseDocumentsCsv, parseTrialBalanceCsv } from '../../utils/csvImport';
import { formatDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

/** ISO date minus one day, without timezone drift (the server does the same arithmetic on the go-live date). */
export const dayBefore = (iso: string): string => {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d - 1));
    return dt.toISOString().slice(0, 10);
};

const OpeningBalances: React.FC = () => {
    const canAccounts = usePermission(PERMISSIONS.ACCOUNT_MANAGE);
    const canCustomers = usePermission(PERMISSIONS.SALES_INVOICE_MANAGE);
    const canSuppliers = usePermission(PERMISSIONS.SUPPLIER_BILL_MANAGE);

    const tabs = useMemo(() => [
        // Every importer also needs ACCOUNT_MANAGE (it posts to the ledger); the document key is on top.
        ...(canAccounts ? ['accounts'] : []),
        ...(canAccounts && canCustomers ? ['customers'] : []),
        ...(canAccounts && canSuppliers ? ['suppliers'] : []),
    ] as ('accounts' | 'customers' | 'suppliers')[], [canAccounts, canCustomers, canSuppliers]);
    const [tab, setTab] = useState<'accounts' | 'customers' | 'suppliers'>('accounts');
    const activeTab = tabs.includes(tab) ? tab : tabs[0];

    const [goLive, setGoLive] = useState('');
    const [offsetToEquity, setOffsetToEquity] = useState(false);
    const [status, setStatus] = useState<OpeningBalanceStatus | null>(null);

    const loadStatus = useCallback(async () => {
        if (!canAccounts) return;
        try { setStatus(await OpeningBalanceService.status()); } catch { setStatus(null); }
    }, [canAccounts]);
    useEffect(() => { loadStatus(); }, [loadStatus]);

    const blocked = goLive ? '' : 'Pick the go-live date first.';
    const journalDate = goLive ? dayBefore(goLive) : '';

    const goLiveControl = (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField data-help="accounting.openingBalances.field.goLive"
                label="Go-live date" type="date" value={goLive} onChange={e => setGoLive(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 200px', maxWidth: 240 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 300px' }}>
                {goLive
                    ? `Opening entries are dated ${formatDate(journalDate)}, the day before go-live.`
                    : 'The first day you will use this system. Balances are brought forward as at the day before.'}
            </Typography>
        </Box>
    );

    const docSummary = (r: OpeningDocumentsResult) => (
        <span>
            {r.createCount} to create ({formatMoney(r.totalAmount)}), {r.existingCount} already present and skipped
            {r.journalDate ? `, dated ${formatDate(r.journalDate)}` : ''}.
        </span>
    );

    const tbSummary = (r: OpeningTrialBalanceResult) => (
        <span>
            Debits {formatMoney(r.totalDebit)} / Credits {formatMoney(r.totalCredit)}
            {r.difference !== 0 && (r.equityOffset !== 0
                ? `; difference ${formatMoney(Math.abs(r.difference))} goes to Opening Balance Equity`
                : `; difference ${formatMoney(Math.abs(r.difference))}`)}
            {r.replacesEntryNumber ? `. Replaces ${r.replacesEntryNumber} (it is reversed, not edited)` : ''}
            {r.entryNumber ? `. Journal ${r.entryNumber}` : ''}.
        </span>
    );

    if (tabs.length === 0) {
        return <Box sx={{ p: 3 }}><Alert severity="info">You do not have permission to load opening balances.</Alert></Box>;
    }

    return (
        <Box data-help-page="accounting.openingBalances.page" sx={{ p: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h5">Opening balances</Typography>
            <Typography variant="body2" color="text.secondary">
                Go live without hand-written journals: load the chart first (Chart of Accounts &gt; Import CSV), then the
                account balances, then each customer's open invoices and each supplier's open bills. Always preview first.
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>{goLiveControl}</Paper>
            <Tabs value={activeTab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
                {canAccounts && <Tab data-help="accounting.openingBalances.tab.accounts" value="accounts" label="Accounts" />}
                {canAccounts && canCustomers && <Tab data-help="accounting.openingBalances.tab.customers" value="customers" label="Customers" />}
                {canAccounts && canSuppliers && <Tab data-help="accounting.openingBalances.tab.suppliers" value="suppliers" label="Suppliers" />}
            </Tabs>
            <Paper variant="outlined" sx={{ p: 2 }}>
                {activeTab === 'accounts' && (
                    <>
                        {status?.posted && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                An opening journal ({status.entryNumber}, {status.date ? formatDate(status.date) : ''}) is already posted.
                                Posting again reverses it and posts a new one; the old entry is never edited.
                            </Alert>
                        )}
                        {status?.closedThrough && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Books are closed through {formatDate(status.closedThrough)}; the go-live date must be later than that.{' '}
                                <RouterLink to="/accounting/ledger-close-settings">Close Books Through</RouterLink>
                            </Alert>
                        )}
                        <OpeningImportPanel<
                            { accountCode: string; debit: string; credit: string }, OpeningTrialBalanceResult>
                            templateKey="trialBalance"
                            description={<>One row per account with its debit or credit balance at the day before go-live. Control
                                accounts (receivables, payables, inventory) are refused here: load customer balances on the
                                Customers tab and supplier balances on the Suppliers tab.</>}
                            parse={parseTrialBalanceCsv}
                            run={(rows, dryRun) => OpeningBalanceService.postTrialBalance(goLive, rows, offsetToEquity, dryRun)}
                            blockedReason={blocked}
                            inputsKey={`${goLive}|${offsetToEquity}`}
                            controls={
                                <FormControlLabel data-help="accounting.openingBalances.field.offsetToEquity"
                                    control={<Checkbox checked={offsetToEquity} onChange={e => setOffsetToEquity(e.target.checked)} />}
                                    label="Post any difference to Opening Balance Equity (otherwise debits must equal credits)"
                                />
                            }
                            renderSummary={tbSummary}
                            postLabel="Post opening balances"
                            confirmText={`Post the opening trial balance dated ${journalDate ? formatDate(journalDate) : ''}? An existing opening journal, if any, is reversed first.`}
                            amountColumns="debitCredit"
                            onCommitted={loadStatus}
                        />
                    </>
                )}
                {activeTab === 'customers' && (
                    <OpeningImportPanel<
                        { partyName: string; documentNumber: string; documentDate: string; dueDate: string; amount: string },
                        OpeningDocumentsResult>
                        templateKey="customers"
                        description={<>Each row becomes an issued opening invoice (Dr Accounts Receivable / Cr Opening Balance
                            Equity). It keeps its own invoice date and due date, so it shows in AR aging and the customer
                            statement and can receive receipts. Customers must already exist.</>}
                        parse={text => parseDocumentsCsv(text, 'customer')}
                        run={(rows, dryRun) => OpeningBalanceService.importCustomers(goLive, rows, dryRun)}
                        blockedReason={blocked}
                        inputsKey={goLive}
                        renderSummary={docSummary}
                        postLabel="Post opening balances"
                        confirmText="Post these opening customer invoices? Rows already present are skipped."
                        amountColumns="amount"
                    />
                )}
                {activeTab === 'suppliers' && (
                    <OpeningImportPanel<
                        { partyName: string; documentNumber: string; documentDate: string; dueDate: string; amount: string },
                        OpeningDocumentsResult>
                        templateKey="suppliers"
                        description={<>Each row becomes an unpaid opening bill (Dr Opening Balance Equity / Cr Accounts Payable)
                            with no purchase order or goods receipt, marked as an opening balance. It shows in payables aging
                            and the supplier statement and is paid through the normal supplier payment. Suppliers must already exist.</>}
                        parse={text => parseDocumentsCsv(text, 'supplier')}
                        run={(rows, dryRun) => OpeningBalanceService.importSuppliers(goLive, rows, dryRun)}
                        blockedReason={blocked}
                        inputsKey={goLive}
                        renderSummary={docSummary}
                        postLabel="Post opening balances"
                        confirmText="Post these opening supplier bills? Rows already present are skipped."
                        amountColumns="amount"
                    />
                )}
            </Paper>
        </Box>
    );
};

export default OpeningBalances;
